/**
 * Preflight: everything that must hold before a document may be saved.
 *
 * The check that matters most is **fragment reachability**. `renderEmail` builds
 * its Twig environment from `DatabaseFragmentLoader` alone — no filesystem
 * fallback — so a body referencing a key the partner's catalog doesn't have
 * throws `LoaderError`. Re-render *catches and logs* that, which means the
 * symptom is not an error the user sees but a **silently stale provider copy**:
 * the template looks saved, and sends keep going out with the previous version.
 *
 * That failure is invisible at every later stage, so it has to be caught here.
 * Since the core components are published by a seeder, an environment where the
 * seeder hasn't run is exactly the case this guards.
 *
 * Reachability is computed from the **compiled body**, not the document, because
 * that is the artifact the server renders: it covers component includes, the
 * layout, `{% include %}` inside hand-written `native.html`, and transitive
 * references from one fragment to another — uniformly, with one walk.
 */
import { EmailDocument } from './document';
import {
  CompileWarning,
  COMPILE_WARNING,
  DEFAULT_LAYOUT_SLOT,
  layoutSlotsUsed,
} from './compile';
import { Registry } from './registry';
import { extractFragmentReferences } from '../insertion';
import { layoutBlocks } from '../../../tabs/Theme/fragmentPreview';

export const PREFLIGHT = {
  /** A referenced fragment key is absent from the catalog. */
  MISSING_FRAGMENT: 'missing-fragment',
  /** `layoutKey` resolves to a fragment that isn't of type `layout`. */
  LAYOUT_NOT_A_LAYOUT: 'layout-not-a-layout',
  /** The document's layout is set but the catalog has no such key. */
  MISSING_LAYOUT: 'missing-layout',
  /** The layout declares no `{% block %}` by the name the compiler emits. */
  LAYOUT_SLOT_NOT_DECLARED: 'layout-slot-not-declared',
  /** A `{% verbatim %}` region carries a variable our own render should resolve. */
  DEFERRED_RENDER_TIME_VAR: 'deferred-render-time-var',
} as const;

export type PreflightSeverity = 'error' | 'warning';

export interface PreflightIssue {
  code: string;
  severity: PreflightSeverity;
  /** Developer-facing; the UI maps `code` to a translated message. */
  detail: string;
  blockId?: string;
  fragmentKey?: string;
}

export interface PreflightResult {
  issues: PreflightIssue[];
  /** True when nothing blocks a save. */
  ok: boolean;
}

export interface PreflightContext {
  /** Fragment key → Twig source, from GET /template-fragments via Redux. */
  fragments?: Record<string, string>;
  /** Fragment key → fragment_type, to verify a layout really is a layout. */
  fragmentTypes?: Record<string, string>;
  registry?: Registry;
}

/**
 * Compile warnings that must block a save, rather than merely inform.
 *
 * A block with no emitter or a missing required prop produces markup that is
 * wrong or absent — saving it ships a broken email. The others describe output
 * that is still valid Twig: a condition that always passes, a prop that was
 * dropped, an `@inline` request served as an include.
 */
const BLOCKING_COMPILE_WARNINGS = new Set<string>([
  COMPILE_WARNING.UNSUPPORTED_BLOCK,
  COMPILE_WARNING.MISSING_REQUIRED_PROP,
]);

/**
 * Every fragment key reachable from `body`, following references transitively
 * through the catalog. Mirrors `collectReachableSources` in the preview
 * renderer, and the server's own resolution order.
 */
function collectReachableKeys(
  body: string,
  layoutKey: string | null,
  fragments: Record<string, string>,
): { reachable: string[]; missing: string[] } {
  const queue: string[] = [...extractFragmentReferences(body)];
  if (layoutKey) queue.push(layoutKey);

  const visited = new Set<string>();
  const missing: string[] = [];

  while (queue.length > 0) {
    const key = queue.shift() as string;
    if (visited.has(key)) continue;
    visited.add(key);

    const source = fragments[key];
    if (source == null) {
      missing.push(key);
      continue;
    }
    // A fragment may pull in others; a gap anywhere in the chain fails the
    // render just as surely as a gap at the top.
    for (const nested of extractFragmentReferences(source)) {
      queue.push(nested);
    }
  }

  return { reachable: [...visited], missing };
}

/**
 * Validates a document and its compiled body against the fragment catalog.
 *
 * Reference checks are skipped entirely when the catalog is empty — that means
 * the fetch hasn't resolved yet, and flagging every real reference as missing
 * would block saves on a loading state. Same guard the code editor's
 * unknown-reference warning uses.
 */
export function preflight(
  document: EmailDocument,
  body: string,
  compileWarnings: CompileWarning[] = [],
  context: PreflightContext = {},
): PreflightResult {
  const issues: PreflightIssue[] = [];
  const fragments = context.fragments ?? {};
  const catalogLoaded = Object.keys(fragments).length > 0;

  for (const warning of compileWarnings) {
    issues.push({
      code: warning.code,
      severity: BLOCKING_COMPILE_WARNINGS.has(warning.code)
        ? 'error'
        : 'warning',
      detail: warning.detail,
      blockId: warning.blockId,
    });
  }

  if (catalogLoaded) {
    const { missing } = collectReachableKeys(
      body,
      document.layoutKey,
      fragments,
    );

    for (const key of missing) {
      const isLayout = key === document.layoutKey;
      issues.push({
        code: isLayout ? PREFLIGHT.MISSING_LAYOUT : PREFLIGHT.MISSING_FRAGMENT,
        severity: 'error',
        fragmentKey: key,
        detail: isLayout
          ? `Layout "${key}" is not in the fragment catalog; emails using this template would fail to render`
          : `No building block is registered under "${key}"; emails using this template would fail to render`,
      });
    }

    // A body that extends a partial rather than a layout renders empty: Twig
    // discards everything outside `{% block %}` tags, so the mistake produces a
    // blank email rather than an error.
    const layoutKey = document.layoutKey;
    const declaredType = layoutKey
      ? context.fragmentTypes?.[layoutKey]
      : undefined;
    const wrongLayoutType =
      layoutKey !== null &&
      declaredType !== undefined &&
      declaredType !== 'layout' &&
      !missing.includes(layoutKey);

    if (layoutKey && wrongLayoutType) {
      issues.push({
        code: PREFLIGHT.LAYOUT_NOT_A_LAYOUT,
        severity: 'error',
        fragmentKey: layoutKey,
        detail: `"${layoutKey}" is a ${declaredType}, not a layout; the email would render empty`,
      });
    }

    /**
     * The generic detector for the failure that has now bitten this build three
     * times: **content emitted into a `{% block %}` the layout never declares is
     * silently discarded by Twig.** No error, no warning — a plausible-looking
     * preview of an empty email.
     *
     * `compile()` wraps a layouted document's content in
     * `{% block <layoutSlot || 'content'> %}`, and `'content'` is a guess.
     * Partner layouts name their blocks after what fills them
     * (`layout/xxxl/base` has `header`, `intro_content`, `footer`, …), so a
     * document pointed at one of those compiles to a block that doesn't exist
     * there. Checking the layout's own source is the only way to catch it before
     * a send. Slot mapping for partner layouts is Sprint 6.5; until then this
     * turns a silent data loss into a blocked save.
     *
     * Suppressed when the layout is missing or isn't a layout at all: those are
     * the same mistake seen closer to its cause, and one root cause should
     * produce one error.
     */
    if (
      layoutKey &&
      !document.rawPassthrough &&
      !missing.includes(layoutKey) &&
      !wrongLayoutType
    ) {
      const layoutSource = fragments[layoutKey];
      const declared = layoutSource ? layoutBlocks(layoutSource) : [];

      /**
       * **Every** region the document fills is checked, not just one. A slot-based
       * layout is filled in several places at once (G8), and a single bad region
       * silently loses only that part of the email — which is harder to notice than
       * losing all of it.
       *
       * An empty document still checks its fallback region, so a template pointed at
       * the wrong layout is caught before anything is placed in it.
       */
      const used = layoutSlotsUsed(document);
      const slots = used.length > 0 ? used : [document.layoutSlot || DEFAULT_LAYOUT_SLOT];

      if (layoutSource !== undefined) {
        for (const slot of slots) {
          if (declared.includes(slot)) continue;
          issues.push({
            code: PREFLIGHT.LAYOUT_SLOT_NOT_DECLARED,
            severity: 'error',
            fragmentKey: layoutKey,
            detail: declared.length
              ? `Layout "${layoutKey}" declares no block named "${slot}" (it has ${declared
                  .map((name) => `"${name}"`)
                  .join(', ')}); that content would be discarded`
              : `Layout "${layoutKey}" declares no blocks at all, so content placed in "${slot}" would be discarded`,
          });
        }
      }
    }
  }

  for (const variable of deferredRenderTimeVars(body)) {
    issues.push({
      code: PREFLIGHT.DEFERRED_RENDER_TIME_VAR,
      severity: 'error',
      detail: `"{{ ${variable} }}" is inside a {% verbatim %} block, so our render leaves it as text and the recipient receives the Twig itself`,
    });
  }

  return {
    issues,
    ok: !issues.some((issue) => issue.severity === 'error'),
  };
}

/**
 * Variables our own render resolves that sit inside a `{% verbatim %}` region.
 *
 * `{% verbatim %}` is how a send-time `{% if %}` survives our push-time render
 * (see sendTime.ts) — but it suppresses *everything* inside it, so a
 * `{{ theme.colors.brand }}` caught in one is emitted as those characters and
 * reaches the inbox as literal Twig. The compiler only ever defers the `{% if %}`
 * tags themselves, never the markup between them, so this catches a
 * hand-written body rather than its own output — which is exactly the case
 * nothing else would.
 */
const VERBATIM_REGION =
  /\{%-?\s*verbatim\s*-?%\}([\s\S]*?)\{%-?\s*endverbatim\s*-?%\}/g;
const RENDER_TIME_TAG =
  /\{\{\s*((?:theme|email_config)(?:\.\w+)*)\s*(?:\|[^{}]*)?\}\}/g;

function deferredRenderTimeVars(body: string): string[] {
  if (!body.includes('verbatim')) return [];

  const found = new Set<string>();
  for (const region of body.matchAll(VERBATIM_REGION)) {
    for (const tag of region[1].matchAll(RENDER_TIME_TAG)) found.add(tag[1]);
  }
  return [...found];
}

export function preflightErrors(result: PreflightResult): PreflightIssue[] {
  return result.issues.filter((issue) => issue.severity === 'error');
}

export function preflightWarnings(result: PreflightResult): PreflightIssue[] {
  return result.issues.filter((issue) => issue.severity === 'warning');
}
